import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { FC } from 'react';

interface DocumentActionDialogProps {
  open: boolean;
  action: 'retry' | 'resolve' | null;
  documentId: number | null;
  reason: string;
  loading: boolean;
  onClose: () => void;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
}

const DocumentActionDialog: FC<DocumentActionDialogProps> = ({
  open,
  action,
  documentId,
  reason,
  loading,
  onClose,
  onReasonChange,
  onConfirm,
}) => {
  const getTitle = () => {
    switch (action) {
      case 'retry':
        return 'Re-process Document';
      case 'resolve':
        return 'Mark Document as Resolved';
      default:
        return 'Document Action';
    }
  };

  const getDescription = () => {
    switch (action) {
      case 'retry':
        return 'This will reset the document state and re-queue it for processing. You can optionally provide a reason for the retry.';
      case 'resolve':
        return 'This will mark the document as manually resolved. Please provide a reason for this action.';
      default:
        return '';
    }
  };

  const isReasonRequired = action === 'resolve';

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {getDescription()}
        </Typography>

        {documentId && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Document ID:</strong> {documentId}
          </Typography>
        )}

        <TextField
          label={isReasonRequired ? 'Reason *' : 'Reason (optional)'}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          fullWidth
          multiline
          rows={3}
          placeholder={
            action === 'retry'
              ? 'Optional: Describe why you are retrying this document...'
              : 'Required: Explain why this document is being marked as resolved...'
          }
          disabled={loading}
          error={isReasonRequired && !reason.trim()}
          helperText={
            isReasonRequired && !reason.trim()
              ? 'Please provide a reason for marking the document as resolved.'
              : ''
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading || (isReasonRequired && !reason.trim())}
          sx={{
            backgroundColor: action === 'retry' ? '#0891b2' : '#10b981',
            '&:hover': {
              backgroundColor: action === 'retry' ? '#0e7490' : '#059669',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : null}
          {action === 'retry' ? 'Re-process' : 'Mark as Resolved'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentActionDialog;